import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { BackButton } from '../components/ui/BackButton';
import { Button } from '../components/ui/Button';
import { Input, checkboxClass } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Label } from '../components/ui/Label';
import { Role } from '../auth/roles';
import { useAuthStore } from '../stores/authStore';

// Short list mirroring the catchment area; country is a free string on the backend. The English
// name is the stored value; the label shown is resolved via relations.countries.* at render time.
const COUNTRIES = [
  'France',
  'Belgium',
  'Luxembourg',
  'Germany',
  'Spain',
  'Italy',
  'Netherlands',
  'Switzerland',
  'United Kingdom',
];

type CustomerType = 'B2B' | 'B2C';

// Billing arrangement ("type client") — distinct from the B2B/B2C customer type.
// Labels: relations.billingTypes.<value>.
const BILLING_TYPES = ['CASH', 'CREDIT', 'ACOMPTE', 'DOMICILIATION'];

/**
 * WP-2 — the payment terms are the billing REGIME (comptant → INV at collection; NET_* → a
 * priced BL per delivery, settled by one monthly recap FAC). `billing_mode` is derived from
 * this on the server and is shown read-only. Labels: relations.paymentTerms.<value>.
 */
const COMPTANT_TERM = 'COMPTANT';
/**
 * A consumer account may be deferred on exactly ONE term: NET_7, invoiced at each sale and
 * due sale date + 7. The longer terms are professional terms — a private individual is not
 * a garage. (Backed by customers_b2c_terms_check, migration 072.)
 */
const B2C_TERMS = [COMPTANT_TERM, 'NET_7'];
/**
 * Professional terms. Deferred B2B is billed by ONE recap invoice dated the last day of the
 * month; the term only changes N in "recap invoice date + N days".
 *
 * NET_45 is the ceiling: art. L441-10 C. com. caps a term at 45 days from the invoice date,
 * so NET_60 is gone. END_OF_MONTH_* is gone too — the recap is already cut at month close, so
 * "n jours fin de mois" buys nothing over NET_n and compounds past the legal ceiling. Both
 * remain readable on legacy rows, they are just no longer selectable.
 */
const B2B_TERMS = [COMPTANT_TERM, 'NET_10', 'NET_30', 'NET_45'];

/** The collection INSTRUMENT — a separate axis from the regime. Labels: relations.settlementChannels.<value>. */
const SETTLEMENT_CHANNELS = [
  'CASH',
  'CARD',
  'CHECK',
  'TRANSFER',
  'CLIENT_PUSHED',
  'SEPA_SDD',
  'LCR',
];
/** Direct debit is reserved for professional accounts. */
const DIRECT_DEBIT_CHANNELS = ['SEPA_SDD', 'LCR'];

/**
 * Add Relation — create/edit a customer. Every field on the relation sheet is persisted:
 * name, siret, vatNumber, contact{email,phone,representativeName}, billing/shipping addresses,
 * the per-purpose notification emails, IBAN, website, billing type, credit period, agreement,
 * webshop discount, and the is-customer / is-supplier / block-sale toggles (migration 031).
 * SIRET / VAT / representative / primary email remain B2B-only.
 */
/**
 * When `embedded` is set, the form renders without page chrome (back button / page width)
 * so it can live inside a slide-in panel; on success it calls onSuccess/onClose instead of
 * navigating. Used by the Customers list "+ New relation" right-side panel and by the order
 * screen's customer picker ("+ New customer").
 *
 * onSuccess receives what was just created so a caller can act on it — the order screen
 * selects it into the active quote. Callers that only need "something changed" (the Customers
 * list, which simply reloads) can keep ignoring the argument.
 */
interface PriceFamily {
  id: string;
  code: string;
  name: string;
  coefficient: string;
  hidden?: boolean;
  defaultFor?: string[];
}

export default function CustomerCreate({
  embedded = false,
  onClose,
  onSuccess,
}: {
  embedded?: boolean;
  onClose?: () => void;
  onSuccess?: (created: { id: string; customerType: CustomerType }) => void;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  // Granting credit is an ADM/MGR/OWN act (3.2, 2026-10-23 — mirrors PUT /customers/:id and the
  // server-side assertMayGrantCredit). Counter staff still create relations — theirs land at 0,
  // and a customer at 0 pays like a retail one.
  const role = useAuthStore((s) => s.user?.role);
  const canSetCredit = role === Role.ADMIN || role === Role.MANAGER || role === Role.OWNER;
  const { showApiError, push } = useToast();
  const [busy, setBusy] = useState(false);
  // Optimistic-lock version for edits (read from GET /customers/:id, echoed on PATCH).
  const [version, setVersion] = useState<number | null>(null);

  // 3.2 (2026-10-23): a new relation opens as a retail customer — the counter's common case, and
  // a garage account is then a deliberate choice rather than an accident.
  const [type, setType] = useState<CustomerType>('B2C');
  const [name, setName] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // 3.3 — a particulier's car, saved as his first vehicle when the relation is created.
  const [licensePlate, setLicensePlate] = useState('');
  const [siret, setSiret] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  // Billing address
  const [address, setAddress] = useState('');
  const [zip, setZip] = useState('');
  const [place, setPlace] = useState('');
  const [country, setCountry] = useState('France');
  // Shipping / delivery address (single line on this form)
  const [deliveryAddress, setDeliveryAddress] = useState('');
  // Account-level relation fields (migration 031).
  const [emailInvoice, setEmailInvoice] = useState('');
  const [emailPackingSlip, setEmailPackingSlip] = useState('');
  const [emailQuotation, setEmailQuotation] = useState('');
  const [emailOrder, setEmailOrder] = useState('');
  const [iban, setIban] = useState('');
  const [website, setWebsite] = useState('');
  const [billingType, setBillingType] = useState('');
  const [creditPeriod, setCreditPeriod] = useState('');
  const [agreement, setAgreement] = useState('');
  const [webshopDiscount, setWebshopDiscount] = useState('');
  const [isCustomer, setIsCustomer] = useState(true);
  const [isSupplier, setIsSupplier] = useState(false);
  const [blockSale, setBlockSale] = useState(false);
  // WP-2 — the payment terms ALONE select the billing regime; the settlement channel is the
  // separate "how we collect" axis. There is no billing-mode input any more: it is derived.
  /* Feedback 3.1 (2026-10-22): a new relation STARTS on Comptant. The select used to open on an
     empty option labelled "Comptant (par défaut)", which looked chosen but sent nothing — and
     payment terms are one of the five mandatory fields, so Save stayed greyed out with every
     visible field filled in. Edits load the stored value (see the effect above). */
  const [paymentTerms, setPaymentTerms] = useState(isEdit ? '' : COMPTANT_TERM);
  // One of the five mandatory fields — the named coefficient grid (K1, K2…). Labelled
  // "Price grid", never "Customer type": the client's note used the same words for both, and
  // confusing them prices the account off the wrong axis.
  // Migration 137 — the family is the single source of a customer's prices. Pre-filled from the
  // customer type (the family whose defaultFor names it: GAR for a garage, GOLD for retail); only
  // the admin or the owner may pick another, and hidden (test) families are never offered at
  // creation. The server enforces both.
  const [priceGridId, setPriceGridId] = useState('');
  const [priceGrids, setPriceGrids] = useState<PriceFamily[]>([]);
  const canSetFamily = role === Role.ADMIN || role === Role.OWNER;
  useEffect(() => {
    apiClient
      .get<{ data: PriceFamily[] }>('/customers/price-grids')
      .then((r) => setPriceGrids(r.data.data ?? []))
      .catch(() => setPriceGrids([]));
  }, []);
  const typeDefault = priceGrids.find((g) => g.defaultFor?.includes(type));
  const offeredFamilies = isEdit ? priceGrids : priceGrids.filter((g) => !g.hidden);
  // Create only: follow the customer type. An edit keeps the account where it is.
  useEffect(() => {
    if (!isEdit && typeDefault) setPriceGridId(typeDefault.id);
  }, [isEdit, typeDefault]);
  const [settlementChannel, setSettlementChannel] = useState('');
  const [clientCode, setClientCode] = useState('');
  // Migration 079 — the credit line, decided HERE rather than in a second step someone can skip.
  // Off + 0 is the safe default: a relation nobody configured cannot order on account. (Before
  // 079 the same omission wrote credit_limit 0, which the engine read as UNLIMITED.)
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditLimit, setCreditLimit] = useState('');

  // Edit mode: prefill from the existing relation and capture its version for the lock.
  useEffect(() => {
    if (!isEdit) return;
    apiClient
      .get(`/customers/${id}`)
      .then((r) => {
        const c = r.data as Record<string, unknown>;
        const contact = (c.contact ?? {}) as Record<string, unknown>;
        const bill = (c.billing_address ?? {}) as Record<string, unknown>;
        const ship = (c.shipping_address ?? {}) as Record<string, unknown>;
        setType(c.customer_type === 'B2C' ? 'B2C' : 'B2B');
        setName(String(c.name ?? ''));
        setRepresentativeName(String(contact.representativeName ?? ''));
        setEmail(String(contact.email ?? ''));
        setPhone(String(contact.phone ?? ''));
        setSiret(String(c.siret ?? ''));
        setVatNumber(String(c.vat_number ?? ''));
        setAddress(String(bill.line1 ?? ''));
        setZip(String(bill.postalCode ?? ''));
        setPlace(String(bill.city ?? ''));
        setCountry(String(bill.country ?? ''));
        setDeliveryAddress(String(ship.line1 ?? ''));
        setEmailInvoice(String(c.email_invoice ?? ''));
        setEmailPackingSlip(String(c.email_packing_slip ?? ''));
        setEmailQuotation(String(c.email_quotation ?? ''));
        setEmailOrder(String(c.email_order ?? ''));
        setIban(String(c.iban ?? ''));
        setWebsite(String(c.website ?? ''));
        setBillingType(String(c.billing_type ?? ''));
        setCreditPeriod(c.credit_period == null ? '' : String(c.credit_period));
        setAgreement(String(c.agreement ?? ''));
        setWebshopDiscount(
          c.webshop_discount_percentage == null ? '' : String(c.webshop_discount_percentage)
        );
        setIsCustomer(c.is_customer !== false);
        setIsSupplier(c.is_supplier === true);
        setBlockSale(c.block_sale === true);
        setPaymentTerms(String(c.payment_terms ?? ''));
        setSettlementChannel(String(c.settlement_channel ?? ''));
        setClientCode(String(c.client_code ?? ''));
        setPriceGridId(String(c.price_grid_id ?? ''));
        setVersion(typeof c.version === 'number' ? c.version : 0);
      })
      .catch(showApiError);
  }, [isEdit, id, showApiError]);

  const b2b = type === 'B2B';

  // WP-2 — the regime, derived from the terms exactly as the backend derives it.
  const deferred = paymentTerms !== '' && !['COMPTANT', 'IMMEDIATE'].includes(paymentTerms);

  const terms = b2b ? B2B_TERMS : B2C_TERMS;
  /**
   * Deferred B2B accumulates priced BLs into one monthly recap; deferred B2C (NET_7) is
   * invoiced at each sale — the recap is a professional instrument only.
   */
  const perSaleDeferred = deferred && !b2b;

  // The two segments offer different terms (NET_7 is B2C-only, NET_10+ is B2B-only), and a
  // B2C account may never hold a direct-debit instrument. Drop a selection that stops being
  // valid when the type flips, either way, rather than carrying it into a submit the backend
  // would refuse.
  useEffect(() => {
    // Comptant exists in both segments, so it is where a no-longer-valid choice falls back to.
    if (paymentTerms !== '' && !terms.includes(paymentTerms)) setPaymentTerms(COMPTANT_TERM);
    if (!b2b && DIRECT_DEBIT_CHANNELS.includes(settlementChannel)) setSettlementChannel('');
  }, [b2b, terms, paymentTerms, settlementChannel]);

  // SIRET must be exactly 14 digits when present (matches the backend regex; avoids a round-trip 400).
  const siretError = b2b && siret.trim() !== '' && !/^\d{14}$/.test(siret.trim());
  // Webshop discount, when present, must parse to 0–100 (matches the backend range).
  const discountError =
    webshopDiscount.trim() !== '' &&
    (Number.isNaN(Number(webshopDiscount)) ||
      Number(webshopDiscount) < 0 ||
      Number(webshopDiscount) > 100);
  // Credit period, when present, must be a non-negative integer number of days.
  const creditPeriodError =
    creditPeriod.trim() !== '' &&
    (!Number.isInteger(Number(creditPeriod)) || Number(creditPeriod) < 0);
  // Switching credit ON means committing to an amount; a blank or 0 amount behind an ON toggle
  // is the same "configured but not really" state the toggle exists to prevent.
  const creditLimitError =
    creditEnabled && (creditLimit.trim() === '' || !(Number(creditLimit) > 0));
  /**
   * A deferred RETAIL account with no credit line is legal but inert — every order is refused
   * with CREDIT_LIMIT_EXCEEDED. Warn, don't block. A garage at zero credit is the normal start
   * (3.2, 2026-10-23): it simply pays like a retail customer until a line is granted.
   */
  const deferredWithoutCredit = !b2b && deferred && !creditEnabled;
  /**
   * What still keeps Save greyed out, said next to it (3.1): a disabled button with no reason is
   * how "every field filled in, it won't save" reached the client.
   */
  /* 3.2 — the fields the client made mandatory to CREATE, per segment (the server refuses the
     same gaps: customerIdentity.assertOnboardingComplete). A particulier: the name and the plate,
     nothing else (2026-10-23) — terms, billing type and price family are defaulted. A garage:
     SIRET, address, e-mail, phone and the commercial fields; no credit amount. An edit keeps the
     five. */
  const onboarding = !isEdit;
  const req = {
    address: onboarding && b2b,
    plate: onboarding && !b2b,
    siret: onboarding && b2b,
    email: onboarding && b2b,
    phone: onboarding && b2b,
    commercial: !onboarding || b2b,
  };
  const noAddress = address.trim() === '' || zip.trim() === '' || place.trim() === '';
  // Same reduction as the server's (letters and digits, 2–15 of them).
  const plateUsable = /^[A-Z0-9]{2,15}$/.test(licensePlate.toUpperCase().replace(/[^A-Z0-9]/g, ''));
  const missing = [
    name.trim() === '' && t('relations.fields.name'),
    req.siret && siret.trim() === '' && t('relations.fields.siret'),
    req.address && noAddress && t('relations.form.addressBlock'),
    req.email && !email.trim() && t('relations.fields.email'),
    req.phone && !phone.trim() && t('relations.fields.phone'),
    req.plate && !plateUsable && t('relations.fields.licensePlate'),
    req.commercial && paymentTerms === '' && t('relations.fields.paymentTerms'),
    req.commercial &&
      billingType === '' &&
      t('relations.form.billingTypeRequired').replace(/\s*\*$/, ''),
    req.commercial &&
      priceGridId === '' &&
      t('relations.form.priceFamilyRequired').replace(/\s*\*$/, ''),
  ].filter(Boolean) as string[];
  const invalid = [
    siretError && t('relations.fields.siret'),
    discountError && t('relations.fields.webshopDiscount'),
    creditPeriodError && t('relations.fields.creditPeriod'),
    creditLimitError && t('relations.fields.creditLimit'),
  ].filter(Boolean) as string[];
  // Edits need the loaded version before we can send the optimistic-lock PATCH.
  const canSubmit =
    missing.length === 0 &&
    name.trim() !== '' &&
    // The five mandatory fields (a particulier's are defaulted). Client-side this only shapes the
    // button; the server refuses an incomplete or placeholder-filled create regardless.
    (!req.commercial || (paymentTerms !== '' && billingType !== '' && priceGridId !== '')) &&
    !siretError &&
    !discountError &&
    !creditPeriodError &&
    !creditLimitError &&
    !busy &&
    (!isEdit || version !== null);

  // Account-level relation fields, sent on both create and edit. Empty strings clear
  // text fields to undefined (left untouched on create / unchanged on edit); the toggles
  // and billing type are always sent.
  const relationExtras = useMemo(() => {
    const e: Record<string, unknown> = {
      isCustomer,
      isSupplier,
      blockSale,
    };
    if (emailInvoice.trim()) e.emailInvoice = emailInvoice.trim();
    if (emailPackingSlip.trim()) e.emailPackingSlip = emailPackingSlip.trim();
    if (emailQuotation.trim()) e.emailQuotation = emailQuotation.trim();
    if (emailOrder.trim()) e.emailOrder = emailOrder.trim();
    if (iban.trim()) e.iban = iban.trim();
    if (website.trim()) e.website = website.trim();
    if (billingType) e.billingType = billingType;
    if (agreement.trim()) e.agreement = agreement.trim();
    if (creditPeriod.trim()) e.creditPeriod = Number(creditPeriod);
    if (webshopDiscount.trim()) e.webshopDiscountPercentage = Number(webshopDiscount);
    if (paymentTerms) e.paymentTerms = paymentTerms;
    if (priceGridId) e.priceGridId = priceGridId;
    if (settlementChannel) e.settlementChannel = settlementChannel;
    if (clientCode.trim()) e.clientCode = clientCode.trim();
    return e;
  }, [
    isCustomer,
    isSupplier,
    blockSale,
    emailInvoice,
    emailPackingSlip,
    emailQuotation,
    emailOrder,
    iban,
    website,
    billingType,
    agreement,
    creditPeriod,
    webshopDiscount,
    paymentTerms,
    priceGridId,
    settlementChannel,
    clientCode,
  ]);

  /**
   * Create-only, and only from an admin. The server accepts `creditLimit` on POST /customers/b2b
   * and /b2c but NOT on the profile PATCH — credit changes on an existing relation go through
   * PUT /customers/:id, which is ADM/OWN-guarded. Omitting the key entirely is what makes the
   * server write the safe default of 0; sending 0 explicitly would mean the same thing here, but
   * a non-admin may not send the field at all, so omission is also what keeps their create legal.
   */
  const creditPayload = useMemo(
    () =>
      // Only an admin, a manager or the owner grants a line (3.2, 2026-10-23); everyone else's
      // create omits the field and lands at zero.
      canSetCredit && creditEnabled ? { creditLimit: Number(creditLimit) } : {},
    [canSetCredit, creditEnabled, creditLimit]
  );

  const billingAddress = useMemo(() => {
    const a: Record<string, string> = {};
    if (address.trim()) a.line1 = address.trim();
    if (zip.trim()) a.postalCode = zip.trim();
    if (place.trim()) a.city = place.trim();
    if (country.trim()) a.country = country.trim();
    return Object.keys(a).length ? a : undefined;
  }, [address, zip, place, country]);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const shippingAddress = deliveryAddress.trim()
        ? { line1: deliveryAddress.trim() }
        : undefined;
      const contact: Record<string, string> = {};
      if (email.trim()) contact.email = email.trim();
      if (phone.trim()) contact.phone = phone.trim();
      if (representativeName.trim()) contact.representativeName = representativeName.trim();

      if (isEdit) {
        // Optimistic-locked profile PATCH; a stale version surfaces a 409 conflict toast.
        await apiClient.patch(
          `/customers/${id}`,
          {
            expectedVersion: version ?? undefined,
            name: name.trim(),
            siret: b2b && siret.trim() ? siret.trim() : undefined,
            vatNumber: b2b && vatNumber.trim() ? vatNumber.trim() : undefined,
            // A particulier's e-mail / phone live in `contact` too since 3.2.
            contact: Object.keys(contact).length ? contact : undefined,
            billingAddress,
            shippingAddress,
            ...relationExtras,
          },
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        push({ tone: 'success', message: t('relations.form.toastUpdated', { name: name.trim() }) });
        navigate(`/customers/${id}`);
        return;
      }

      // Both create endpoints answer 201 with { id, customerType } — captured so an embedded
      // caller can act on the new relation instead of re-searching for it by name.
      let created: { id: string; customerType: CustomerType };
      if (b2b) {
        const { data } = await apiClient.post<{ id: string; customerType: CustomerType }>(
          '/customers/b2b',
          {
            name: name.trim(),
            siret: siret.trim() || undefined,
            vatNumber: vatNumber.trim() || undefined,
            contact: Object.keys(contact).length ? contact : undefined,
            billingAddress,
            shippingAddress,
            ...relationExtras,
            ...creditPayload,
          },
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        created = data;
      } else {
        const { data } = await apiClient.post<{ id: string; customerType: CustomerType }>(
          '/customers/b2c',
          {
            name: name.trim(),
            phone: phone.trim() || undefined,
            email: email.trim() || undefined,
            licensePlate: licensePlate.trim() || undefined,
            billingAddress,
            shippingAddress,
            ...relationExtras,
            ...creditPayload,
          },
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        created = data;
      }
      push({ tone: 'success', message: t('relations.form.toastCreated', { name: name.trim() }) });
      if (embedded) {
        onSuccess?.(created);
        onClose?.();
        return;
      }
      navigate('/customers');
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {!embedded && (
        <BackButton to="/customers" label={t('relations.form.backToCustomers')} className="mb-0" />
      )}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b pb-4">
        <h1 className="text-xl font-semibold tracking-tight">
          {isEdit ? t('relations.form.titleEdit') : t('relations.form.titleNew')}
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="success" size="sm" disabled={!canSubmit} onClick={submit}>
            <Save className="h-4 w-4" />
            {busy ? t('relations.form.saving') : t('relations.form.save')}
          </Button>
          {embedded && onClose && (
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label={t('relations.form.close')}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </header>
      {(missing.length > 0 || invalid.length > 0) && (
        <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          {missing.length > 0 && t('relations.form.missingFields', { fields: missing.join(', ') })}
          {missing.length > 0 && invalid.length > 0 && ' '}
          {invalid.length > 0 && t('relations.form.invalidFields', { fields: invalid.join(', ') })}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{t('relations.form.b2bOnlyIntro')}</p>
      {/*
        Deferred terms grant credit; a 0 ceiling refuses it. That combination is legal (the
        ceiling may be pending an admin) but every order will be refused until one is set, so
        say it here rather than letting the operator discover it at the first 422.
      */}
      {!isEdit && deferredWithoutCredit && (
        <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          {t('relations.form.deferredWithoutCredit', {
            who: canSetCredit
              ? t('relations.form.deferredWithoutCreditYou')
              : t('relations.form.deferredWithoutCreditAdmin'),
          })}
        </p>
      )}

      <section className="border rounded p-4 bg-card space-y-4">
        {/* Name is required to create and isn't in either column on the source sheet — kept on top. */}
        <div className="max-w-md space-y-3">
          <Field
            label={t('relations.fields.customerType')}
            hint={isEdit ? t('relations.form.cannotChangeType') : t('relations.form.typeHint')}
          >
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as CustomerType)}
              disabled={isEdit}
            >
              <option value="B2C">{t('relations.form.typeB2C')}</option>
              <option value="B2B">{t('relations.form.typeB2B')}</option>
            </Select>
          </Field>
          <Field label={t('relations.fields.name')} required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('relations.fields.name')}
            />
          </Field>
          {/* 3.2 / 3.3 — the plate of a retail customer's car, right below the name. */}
          {req.plate && (
            <Field label={t('relations.fields.licensePlate')} required>
              <Input
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                placeholder="AB-123-CD"
                className="font-mono"
              />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-3">
          {/* ---------- Column 1 ---------- */}
          <div className="space-y-3">
            <Field
              label={t('relations.fields.representativeName')}
              hint={b2b ? undefined : t('relations.form.b2bOnly')}
            >
              <Input
                value={representativeName}
                onChange={(e) => setRepresentativeName(e.target.value)}
                placeholder={t('relations.fields.representativeName')}
                disabled={!b2b}
              />
            </Field>
            <Field label={t('relations.fields.address')} required={req.address}>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t('relations.fields.address')}
              />
            </Field>
            <Field label={t('relations.fields.deliveryAddress')}>
              <Input
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder={t('relations.fields.deliveryAddress')}
              />
            </Field>
            <Field label={t('relations.fields.zipCode')} required={req.address}>
              <Input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder={t('relations.fields.zipCode')}
              />
            </Field>
            <Field label={t('relations.fields.place')} required={req.address}>
              <Input
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder={t('relations.fields.place')}
              />
            </Field>
            <Field label={t('relations.fields.country')}>
              <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="">{t('relations.form.selectCountry')}</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`relations.countries.${c}`, { defaultValue: c })}
                  </option>
                ))}
              </Select>
            </Field>
            {/* A particulier gives an e-mail OR a phone (3.2); a garage gives both. */}
            <Field label={t('relations.fields.email')} required={req.email}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('relations.form.emailPlaceholder')}
              />
            </Field>
            <Field label={t('relations.fields.emailInvoice')}>
              <Input
                type="email"
                value={emailInvoice}
                onChange={(e) => setEmailInvoice(e.target.value)}
                placeholder={t('relations.fields.emailInvoice')}
              />
            </Field>
            <Field label={t('relations.fields.emailPackingSlip')}>
              <Input
                type="email"
                value={emailPackingSlip}
                onChange={(e) => setEmailPackingSlip(e.target.value)}
                placeholder={t('relations.fields.emailPackingSlip')}
              />
            </Field>
            <Field label={t('relations.fields.emailQuotation')}>
              <Input
                type="email"
                value={emailQuotation}
                onChange={(e) => setEmailQuotation(e.target.value)}
                placeholder={t('relations.fields.emailQuotation')}
              />
            </Field>
            <Field label={t('relations.fields.emailOrder')}>
              <Input
                type="email"
                value={emailOrder}
                onChange={(e) => setEmailOrder(e.target.value)}
                placeholder={t('relations.fields.emailOrder')}
              />
            </Field>
            <Field label={t('relations.fields.phone')} required={req.phone}>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('relations.fields.phone')}
              />
            </Field>
          </div>

          {/* ---------- Column 2 ---------- */}
          <div className="space-y-3">
            <Field
              label={t('relations.fields.siret')}
              hint={
                siretError
                  ? t('relations.form.siretRequired')
                  : b2b
                    ? req.siret
                      ? t('relations.form.siretRequired')
                      : t('relations.form.siretOptional')
                    : t('relations.form.b2bOnly')
              }
              error={siretError}
              required={req.siret}
            >
              <Input
                value={siret}
                onChange={(e) => setSiret(e.target.value)}
                placeholder={t('relations.fields.siret')}
                disabled={!b2b}
                inputMode="numeric"
              />
            </Field>
            <Field label={t('relations.fields.iban')}>
              <Input
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                placeholder={t('relations.fields.iban')}
              />
            </Field>
            <Field
              label={t('relations.fields.vatNumber')}
              hint={b2b ? undefined : t('relations.form.b2bOnly')}
            >
              <Input
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder={t('relations.fields.vatNumber')}
                disabled={!b2b}
              />
            </Field>
            <Field label={t('relations.fields.website')}>
              <Input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder={t('relations.fields.website')}
              />
            </Field>
            <Field
              label={
                req.commercial
                  ? t('relations.form.billingTypeRequired')
                  : t('relations.form.billingTypeOptional')
              }
            >
              <Select value={billingType} onChange={(e) => setBillingType(e.target.value)}>
                <option value="">{t('relations.form.selectBillingType')}</option>
                {BILLING_TYPES.map((b) => (
                  <option key={b} value={b}>
                    {t(`relations.billingTypes.${b}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {/* One of the five mandatory fields. Deliberately labelled "Price grid" — it is the
                coefficient axis, not the B2B/B2C axis above. */}
            <Field
              label={
                req.commercial
                  ? t('relations.form.priceFamilyRequired')
                  : t('relations.form.priceFamilyOptional')
              }
              hint={
                canSetFamily
                  ? t('relations.form.priceFamilyHintAdmin')
                  : t('relations.form.priceFamilyHintLocked')
              }
            >
              <Select
                value={priceGridId}
                disabled={!canSetFamily && (isEdit || !!typeDefault)}
                onChange={(e) => setPriceGridId(e.target.value)}
              >
                <option value="">{t('relations.form.selectPriceFamily')}</option>
                {offeredFamilies.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.code} — {g.name} (×{Number(g.coefficient).toFixed(2)})
                    {g.hidden ? t('relations.form.testFamily') : ''}
                  </option>
                ))}
              </Select>
            </Field>
            {/* WP-2 — the payment terms ALONE select the billing regime. */}
            <Field
              label={t('relations.fields.paymentTerms')}
              hint={
                perSaleDeferred
                  ? t('relations.form.termsHintPerSale')
                  : deferred
                    ? t('relations.form.termsHintDeferred')
                    : t('relations.form.termsHintComptant')
              }
            >
              <Select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)}>
                {/* Only an old record can have no terms; a new one starts on Comptant. */}
                {paymentTerms === '' && <option value="">{t('relations.form.selectTerms')}</option>}
                {terms.map((term) => (
                  <option key={term} value={term}>
                    {t(`relations.paymentTerms.${term}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {/*
              The credit line, decided on the creation screen rather than in a second step.
              Off + 0 is the safe default; it is also what a non-admin always gets. Hidden
              entirely on edit: credit changes on an existing relation go through the
              ADM/OWN-guarded configure endpoint on the relation page, not this form.
            */}
            {!isEdit && canSetCredit && (
              <Field
                label={t('relations.fields.creditLine')}
                hint={
                  creditEnabled
                    ? t('relations.form.creditHintEnabled')
                    : b2b
                      ? t('relations.form.creditHintB2bZero')
                      : t('relations.form.creditHintDisabled')
                }
                error={creditLimitError}
              >
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={creditEnabled}
                      onChange={(e) => {
                        setCreditEnabled(e.target.checked);
                        if (!e.target.checked) setCreditLimit('');
                      }}
                    />
                    {t('relations.form.grantCredit')}
                  </label>
                  <Input
                    value={creditEnabled ? creditLimit : '0'}
                    onChange={(e) => setCreditLimit(e.target.value)}
                    disabled={!creditEnabled}
                    placeholder={t('relations.form.amountPlaceholder')}
                    inputMode="decimal"
                  />
                </div>
              </Field>
            )}
            <Field
              label={t('relations.fields.billingMode')}
              hint={t('relations.form.billingModeHint')}
            >
              <Input
                value={
                  perSaleDeferred
                    ? t('relations.billingModes.deferredPerSale')
                    : deferred
                      ? t('relations.billingModes.deferredMonthly')
                      : t('relations.billingModes.immediate')
                }
                disabled
                readOnly
              />
            </Field>
            {/* The instrument, a separate axis from the regime. Direct debit is B2B-only. */}
            <Field
              label={t('relations.fields.settlementChannel')}
              hint={
                b2b ? t('relations.form.settlementHintB2B') : t('relations.form.settlementHintB2C')
              }
            >
              <Select
                value={settlementChannel}
                onChange={(e) => setSettlementChannel(e.target.value)}
              >
                <option value="">{t('relations.form.settlementManual')}</option>
                {SETTLEMENT_CHANNELS.filter((c) => b2b || !DIRECT_DEBIT_CHANNELS.includes(c)).map(
                  (c) => (
                    <option key={c} value={c}>
                      {t(`relations.settlementChannels.${c}`)}
                    </option>
                  )
                )}
              </Select>
            </Field>
            <Field
              label={t('relations.fields.clientCode')}
              hint={t('relations.form.clientCodeHint')}
            >
              <Input
                value={clientCode}
                onChange={(e) => setClientCode(e.target.value)}
                placeholder={t('relations.form.clientCodePlaceholder')}
                maxLength={40}
              />
            </Field>
            <Field
              label={t('relations.fields.creditPeriod')}
              hint={
                creditPeriodError ? t('relations.form.creditPeriodError') : t('relations.form.days')
              }
              error={creditPeriodError}
            >
              <Input
                value={creditPeriod}
                onChange={(e) => setCreditPeriod(e.target.value)}
                placeholder={t('relations.fields.creditPeriod')}
                inputMode="numeric"
              />
            </Field>
            <Field label={t('relations.fields.agreement')}>
              <Input
                value={agreement}
                onChange={(e) => setAgreement(e.target.value)}
                placeholder={t('relations.fields.agreement')}
              />
            </Field>
            <Field
              label={t('relations.fields.webshopDiscountPercentage')}
              hint={discountError ? t('relations.form.discountError') : undefined}
              error={discountError}
            >
              <Input
                value={webshopDiscount}
                onChange={(e) => setWebshopDiscount(e.target.value)}
                placeholder={t('relations.fields.webshopDiscountPercentage')}
                inputMode="decimal"
              />
            </Field>
            <Toggle
              label={t('relations.fields.isCustomer')}
              checked={isCustomer}
              onChange={setIsCustomer}
            />
            <Toggle
              label={t('relations.fields.isSupplier')}
              checked={isSupplier}
              onChange={setIsSupplier}
            />
            {/* Enforced on new orders since migration 151 — the owner's decision. */}
            <Toggle
              label={t('relations.fields.blockSale')}
              checked={blockSale}
              onChange={setBlockSale}
              disabled={role !== 'OWNER'}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && (
        <p className={`text-xs ${error ? 'text-destructive' : 'text-muted-foreground'}`}>{hint}</p>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-sm">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={checkboxClass}
      />
    </label>
  );
}
